import {
  css, ThemeProvider,
} from '@emotion/react';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  type FC, type ReactNode, useRef,
} from 'react';
import {
  AnimatedHeroLayoutComponent, HERO_LAYOUT_TWO_COLUMN_BREAKPOINT,
} from '~/_shared/backgrounds/animatedHeroLayoutComponent';
import { LogoComponent } from '~/_shared/components/logo/logo.component';
import {
  getLogoTagLine, LogoVersion,
} from '~/_shared/components/logo/logo.helpers';
import {
  fadeHeightVariableName, OverlayScrollbarComponent,
} from '~/_shared/components/scrollbar/overlayScrollbar.component';
import { ScrollbarType } from '~/_shared/components/scrollbar/scrollbar.component';
import { darkTheme } from '~/_shared/themes/dark.theme';
import type { Theme } from '~/_shared/themes/theme.model';

const MIN_HEIGHT_FOR_CONSTRAINED_LAYOUT = 600;
const FORM_MAX_WIDTH = 490;
const SCROLL_FADE_HEIGHT = 40;

const formColumnStyle = css({
  width: '100%',
  display: 'flex',
  padding: '20px 10px',
  justifyContent: 'center',
  alignItems: 'center',
  [`@media (min-width: ${HERO_LAYOUT_TWO_COLUMN_BREAKPOINT}px)`]: {
    width: '55%',
  },
  [`@media (min-width: ${HERO_LAYOUT_TWO_COLUMN_BREAKPOINT}px) and (min-height: ${MIN_HEIGHT_FOR_CONSTRAINED_LAYOUT}px)`]: {
    height: 'calc(100vh - 40px)',
  },
});

const formWrapperLayoutStyle = css({
  boxSizing: 'border-box',
  margin: '0 auto',
  minWidth: 100,
  maxWidth: FORM_MAX_WIDTH,
  width: '100%',
});

const formWrapperStyle = css({
  background: 'rgba(17, 27, 34, .3)',
  borderRadius: 8,
  padding: '30px 20px 20px',
});

const scrollbarFadeStyle = css({
  [fadeHeightVariableName]: `${SCROLL_FADE_HEIGHT}px`,
  '&:before, &:after': css(formWrapperLayoutStyle),
});

const logoWrapperStyle = css({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 32,
  alignItems: 'center',
});

const taglineStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  cursor: 'default',
  fontSize: 12,
});

type BrandedAuthPageLayoutProps = Readonly<{
  children?: ReactNode;
  logoVersion?: LogoVersion;
}>;

export const BrandedAuthPageLayoutComponent: FC<BrandedAuthPageLayoutProps> = ({
  children,
  logoVersion = LogoVersion.variantB,
}) => {
  const tagLine = getLogoTagLine(logoVersion);
  const scrollbarRef = useRef<OverlayScrollbarsComponentRef | null>(null);

  return (
    <ThemeProvider theme={darkTheme}>
      <AnimatedHeroLayoutComponent>
        <div css={formColumnStyle}>
          <OverlayScrollbarComponent
            maxHeight="calc(100vh - 80px)"
            type={ScrollbarType.Vertical}
            ref={scrollbarRef}
            showScrollFade
            css={[scrollbarFadeStyle]}
          >
            <div css={[formWrapperLayoutStyle, formWrapperStyle]}>
              <div css={logoWrapperStyle}>
                <LogoComponent width="210px" logoVersion={logoVersion} />
                {tagLine && (
                  <div css={taglineStyle}>
                    {tagLine}
                  </div>
                )}
              </div>
              {children}
            </div>
          </OverlayScrollbarComponent>
        </div>
      </AnimatedHeroLayoutComponent>
    </ThemeProvider>
  );
};
